/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/no-unused-class-component-methods */
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { handleAxiosError } from 'core/notifications';
import { getIsCoursePublishing, useDebounce, useGlobalQueries } from '@/exceed';
import { getFileNameFromAwsUrl } from '@/files';

import handleUpdateVideoProperties from '../helpers/handleUpdateVideoProperties';
import handleUpdateCoverArt from '../helpers/handleUpdateCoverArt';
import getVideosAdminComposeSchema from '../schemas/getVideosAdminComposeSchema';
import getVideosAdminComposeCoverArtSchema from '../schemas/getVideosAdminComposeCoverArtSchema';

import VideosAdminCompose from '../components/videosAdminCompose';
import useReplaceVideo from '../hooks/useReplaceVideo';
import useVideoCompose from '../hooks/useVideoCompose';
import useVideoComposeList from '../hooks/useVideoComposeList';
import useVideoVersion from '../hooks/useVideoVersion';

import getLiveVideoComposeListProps from '../helpers/getLiveVideoComposeListProps';

const VideosAdminComposeContainer = () => {
  const { adminSettings, currentUser } = useGlobalQueries();
  const queryClient = useQueryClient();
  const { shouldForceRender, videoCompose, videoComposeKey } = useVideoCompose();
  const { videoVersion, videoVersionKey } = useVideoVersion({ isEnabled: false });
  const params = {
    params: useParams(),
    queryClient,
    videoVersionKey,
    versionId: videoVersion.data?.draft_id,
  };
  const { type } = params.params;
  const { videoComposeList } = useVideoComposeList({ isEnabled: type === 'live' });

  const debouncedAutoSaveHandler = useDebounce(({ update }) => {
    handleUpdateVideoProperties({
      ...params,
      update,
      handleBackendErrors: handleAxiosError,
    });
  }, 600);

  const onFormUpdate = ({ update }) => {
    queryClient.setQueryData(videoComposeKey, (oldData) => ({ ...oldData, ...update }));
    debouncedAutoSaveHandler({ update });
  };

  const onCoverArtUpdate = ({ update }) => {
    queryClient.setQueryData(videoComposeKey, (oldData) => ({ ...oldData, ...update }));
    handleUpdateCoverArt({
      ...params,
      update: {
        url: update.cover_art,
        name: getFileNameFromAwsUrl({ url: update.cover_art }),
      },
    });
  };

  const updateCache = (data) => {
    queryClient.setQueryData(videoComposeKey, (oldData) => ({
      ...oldData,
      video: data.video,
      ...(data.video_source && { video_source: data.video_source }),
    }));
  };

  const { onReplaceVideoClick, isReplacing } = useReplaceVideo({
    ...params,
    updateCache,
    videoVersion,
  });
  const [isDeletingAgendaItem, setIsDeletingAgendaItem] = useState(false);

  const isLiveVideo = type === 'live';
  const schema = getVideosAdminComposeSchema({ videoCompose, videoVersion });
  const isEditingDisabled = getIsCoursePublishing(videoVersion);
  const coverArtSchema = getVideosAdminComposeCoverArtSchema({ isDisabled: isEditingDisabled });
  const model = videoCompose.data;
  const hasComposeList = Object.keys(videoComposeList.data.items).length > 0;
  const shouldShowForm = !videoCompose.isPlaceholderData && !shouldForceRender;
  const shouldHidePlayer = !videoCompose.data?.video;
  const shouldHideReplace = !isLiveVideo || videoCompose.data?.video;
  const draftState = videoVersion.data.state;
  const courseId = draftState ? videoVersion.data.draft_id : videoVersion.data.video_id;

  return (
    <VideosAdminCompose
      courseId={courseId}
      draftState={draftState}
      shouldShowForm={shouldShowForm}
      shouldHidePlayer={shouldHidePlayer}
      shouldHideReplace={shouldHideReplace}
      onFormUpdate={onFormUpdate}
      onCoverArtUpdate={onCoverArtUpdate}
      schema={schema}
      coverArtSchema={coverArtSchema}
      model={model}
      video={videoCompose.data}
      onReplaceVideoClick={onReplaceVideoClick}
      isReplacing={isReplacing}
      videoComposeList={videoComposeList}
      isLiveVideo={isLiveVideo}
      isEditingDisabled={isEditingDisabled}
      {...(hasComposeList &&
        getLiveVideoComposeListProps({
          adminSettings,
          currentUser,
          isEditingDisabled,
          ...params,
          videoComposeList,
          isDeletingAgendaItem,
          setIsDeletingAgendaItem,
        }))}
    />
  );
};

export default VideosAdminComposeContainer;
